// constants
// const stepFidelity = 960

const calculateGCD = (a, b) => {
  if (b === 0) {
    return a
  }
  return calculateGCD(b, a % b)
}

const calculateLCM = (a, b) => {
  return (a * b) / calculateGCD(a, b)
}

const calculateStepFidelity = (json) => {
  const notes = []

  json.measures.forEach((measure) => {
    measure.notes.forEach((note) => {
      notes.push({
        length: note.length,
        roll: note?.roll,
      })
    })
  })

  const denominators = []

  notes.forEach(item => {

    if (!item || item == 'undefined' || !item?.length) return

    let tupletValue = 1
    let noteValue = 1
    let isDotted = false

    if (item.length.includes('d-')) {
      isDotted = true
      const note = item.length.slice(2)
      noteValue = noteValues[note]
    } else if (item.length.includes('-')) {
      const [tuplet, note] = item.length.split('-')
      tupletValue = parseInt(tuplet, 10)
      noteValue = noteValues[note]
    } else {
      noteValue = noteValues[item.length]
    }

    if (!isNaN(tupletValue) && noteValue) {
      let denominator = tupletValue * noteValue
      if (isDotted) {
        denominator *= 1.5
      }
      if (item?.roll) {
        if (item.length.includes('d-c')) {
          denominator *= item.roll * 2
        } else if (item.length.includes('d-m')) {
          denominator *= item.roll * 4
        } else if (item.length.includes('m')) {
          denominator *= item.roll * 2
        } else if (item.length.includes('w')) {
          denominator *= item.roll * 4
        } else {
          denominator *= item.roll
        }
      }
      denominators.push(denominator)
    }
  })

  // Calculate the LCM of all the denominators
  const lcm = denominators.reduce((acc, denominator) => {
    return calculateLCM(acc, denominator)
  }, 1)

  return lcm
}

const noteValues = {
  'w': 1,
  'm': 2,
  'c': 4,
  'q': 8,
  's': 16,
  'd': 32,
  'h': 64,
}

export const calculateStepsPerMeasure = (beatsPerBar, subdivision, stepFidelity) => {
  return stepFidelity / 4 * beatsPerBar * (4 / subdivision)
}

const calculateStepsForNoteLength = (length, stepFidelity) => {
  const stepMap = {
    
    // legend
    // w: whole note
    // m: half note
    // c: quarter note
    // q: eighth note
    // s: sixteenth note
    // d: thirty-second note
    // h: sixty-fourth note
    // 'd-': dotted note
    // '3-': triplet note
    // '5-': quintuplet note
    // '6-': sextuplet note

    'w': stepFidelity,
    'm': stepFidelity / 2,
    'c': stepFidelity / 4,
    'q': stepFidelity / 8,
    's': stepFidelity / 16,
    'd': stepFidelity / 32,
    'h': stepFidelity / 64,

    // dotted notes
    'd-w': stepFidelity * 1.5,
    'd-m': stepFidelity / 2 * 1.5,
    'd-c': stepFidelity / 4 * 1.5,
    'd-q': stepFidelity / 8 * 1.5,
    'd-s': stepFidelity / 16 * 1.5,
    'd-d': stepFidelity / 32 * 1.5,
    'd-h': stepFidelity / 64 * 1.5,

    // triplets
    '3-m': stepFidelity / 3,
    '3-c': stepFidelity / 6,
    '3-q': stepFidelity / 12,
    '3-s': stepFidelity / 24,
    '3-d': stepFidelity / 48,
    '3-h': stepFidelity / 96,

    // triplets (dotted)
    '3-d-m': stepFidelity / 3 * 1.5,
    '3-d-c': stepFidelity / 6 * 1.5,
    '3-d-q': stepFidelity / 12 * 1.5,
    '3-d-s': stepFidelity / 24 * 1.5,
    '3-d-d': stepFidelity / 48 * 1.5,
    '3-d-h': stepFidelity / 96 * 1.5,

    // quintuplets
    '5-c': stepFidelity / 5,
    '5-q': stepFidelity / 10,
    '5-s': stepFidelity / 20,
    '5-d': stepFidelity / 40,
    '5-h': stepFidelity / 80,

    // sextuplets
    '6-c': stepFidelity / 6,
    '6-q': stepFidelity / 12,
    '6-s': stepFidelity / 24,
    '6-d': stepFidelity / 48,
    '6-h': stepFidelity / 96,

    // septuplets
    '7-c': stepFidelity / 7,
    '7-q': stepFidelity / 14,
    '7-s': stepFidelity / 28,
    '7-d': stepFidelity / 56,
    '7-h': stepFidelity / 112,

    // nontuplets
    '9-c': stepFidelity / 9,
    '9-q': stepFidelity / 18,
    '9-s': stepFidelity / 36,
    '9-d': stepFidelity / 72,
    '9-h': stepFidelity / 144,

  }

  return stepMap[length]
}

export const calculateMeasureDuration = (beatsPerBar, subdivision, tempo) => {
  const secondsPerBeat = (60 / tempo * (4 / subdivision))
  return secondsPerBeat * beatsPerBar
}

export const convertJsonToSteps = json => {
  if (!json || json == 'undefined') return

  const sequence = []
  let globalBeatsPerBar = json.beatsPerBar || 4 // Default to 4/4 if not provided
  let globalSubdivision = json.subdivision || 4 // Default to quarter notes if not provided

  let beatsPerBar = globalBeatsPerBar
  let subdivision = globalSubdivision
  let stepFidelity = calculateStepFidelity(json)

  json.measures.forEach((measure, index) => {

    let numStepsInMeasure = 0

    if ('beatsPerBar' in measure) beatsPerBar = measure.beatsPerBar
    if ('subdivision' in measure) subdivision = measure.subdivision

    const stepsPerMeasure = calculateStepsPerMeasure(beatsPerBar, subdivision, stepFidelity)
    const measureSteps = new Array(stepsPerMeasure).fill({})
    // console.log(stepsPerMeasure)
    let currentStep = 0

    measure.notes.forEach(note => {

      // console.log('note: ', note)

      let stepsToFill = calculateStepsForNoteLength(note.length, stepFidelity)

      if (note?.roll) {

        let noteroll = note.roll
        if (note.length.includes('c')) {
          noteroll = note.roll
        } else if (note.length.includes('m')) {
          noteroll = note.roll * 2
        } else if (note.length.includes('w')) {
          noteroll = note.roll * 4
        } 

        let stepsPerRoll = stepsToFill / noteroll

        // for dotted notes, adjust the steps per roll
        if (note.length.includes('d-')) {
          stepsPerRoll = stepsToFill / (noteroll*1.5)
        }

        for (let i = 0; i < stepsToFill; i++) {
          if (i % stepsPerRoll === 0) {
            if (i === 0) {
              note.callback = true
            } else {
              note.callback = false
            }
            if (note.accent) {
              if (i === 0) {
                measureSteps[currentStep] = { ...note }
              } else {
                delete note.accent
                measureSteps[currentStep] = { ...note }
              }
            } else {
              measureSteps[currentStep] = { ...note }
            }
          }
          currentStep++
        }

      } else {
        for (let i = 0; i < stepsToFill; i++) {
          if (i === 0 && !note.rest) {
            note.callback = true
            measureSteps[currentStep] = { ...note }
          }
          currentStep++
        }
      }

      numStepsInMeasure += stepsToFill

    })

    if (numStepsInMeasure !== stepsPerMeasure) {
      console.log('Error: the number of steps in bar (index) ' + index + ' is invalid: ' + numStepsInMeasure + ' instead of ' + stepsPerMeasure )
    }

    sequence.push({
      beatsPerBar, 
      subdivision, 
      notes: measureSteps 
    })

  })

  // console.log('stepFidelity', stepFidelity)
  // console.log('sequence', sequence)
  
  return {
    sequence,
    stepFidelity
  }
}

export const calculateTimeOffset = (type, tempo) => {
  // Base offset at medium tempo (120 BPM)
  const BASE_OFFSET = 0.025 // 30ms

  // Calculate a scaling factor that increases as tempo decreases
  // At 120 BPM this will be 1.0
  // At 60 BPM this will be around 0.7
  // At 240 BPM this will be around 1.3
  const tempoScalingFactor = Math.pow(tempo / 120, -0.3)
  
  const scaledOffset = BASE_OFFSET * tempoScalingFactor
  
  const offsetMap = {
    'flam': scaledOffset,
    'drag': scaledOffset,
    'ruff': scaledOffset,
  }

  return offsetMap[type] || 0
}

